
/**
 * Module dependencies.
 */

import { Product as BaseProduct, Media, VideoSource } from 'slyk-web-libs/types';
import { Container } from 'client/components/core/container';
import { DefaultProductItem } from 'client/components/core/products/default-product-item';
import { Heading, Link, Text } from 'slyk-web-libs/design-system';
import { ProductItem } from 'client/components/core/products/product-item';
import { ifProp } from 'styled-tools';
import { isEmpty, range } from 'lodash';
import { media } from 'react-components/styles';
import { useTranslate } from 'slyk-web-libs/i18n';
import React from 'react';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import styled, { css } from 'styled-components';

/**
 * `Product` type.
 */

type Product = BaseProduct & {
  displayPrice: string;
  displayPriceConverted: string;
  gallery: Array<Media>;
  video: VideoSource | null;
};

/**
 * `Props` type.
 */

type Props = {
  products: Array<Product>;
};

/*
 * `StyledContainer` styled component.
 */

const StyledContainer = styled(Container)`
  ${media.min('xl')`
    padding-left: 0;
    padding-right: 0;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(12, 1fr);
  grid-template-rows: repeat(3, max-content);
  padding-bottom: 56px;

  ${media.min('sm')`
    gap: 24px;
    grid-template-rows: repeat(2, max-content);
    padding-bottom: 80px;
  `}

  ${media.min('lg')`
    column-gap: 32px;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.div`
  display: flex;
  grid-column: 1 / -1;
  grid-row: 1;
  justify-content: space-between;

  ${media.min('xl')`
    grid-column: 2 / -2;
  `}
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h3', level: 'h4' })`
  font-weight: 600;
`;

/**
 * `ViewAll` styled component.
 */

const ViewAll = styled(Text).attrs({ as: 'span', size: 'small' })`
  font-weight: 700;
`;

/**
 * `Products` styled component.
 */

const Products = styled.ul`
  display: grid;
  gap: 16px;
  grid-column: 1 / -1;
  grid-row: 2;
  grid-template-columns: 1fr;

  ${media.min('xxs')`
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('ms')`
    gap: 24px;
    grid-template-columns: repeat(4, 1fr);
  `}

  ${media.min('xl')`
    gap: 32px;
    grid-column: 2 / -2;
    grid-template-columns: repeat(5, 1fr);
  `}
`;

/**
 * `ProductWrapper` styled component.
 */

const ProductWrapper = styled.li<{ removeLastChild: boolean }>`
  ${ifProp('removeLastChild', css`
    &:last-child {
      display: none;
    }
  `)};

  ${media.min('xl')`
    &:last-child {
      display: block;
    }
  `}
`;

/**
 * Max items.
 */

const maxItems = 5;

/**
 * Export `MoreProducts` component.
 */

export function MoreProducts({ products }: Props): JSX.Element | null {
  const { translate } = useTranslate();
  const productsLength = products?.length ?? 0;
  const defaultProducts = maxItems - productsLength % maxItems;
  const showDefaultProducts = productsLength < maxItems && defaultProducts !== maxItems;

  if (isEmpty(products)) {
    return null;
  }

  return (
    <StyledContainer>
      <Grid>
        <TitleWrapper>
          <Title>
            {translate('products.moreProducts.title')}
          </Title>

          <Link to={payspaceRoutes.products.base}>
            <ViewAll>
              {translate('common.labels.viewAll')}
            </ViewAll>
          </Link>
        </TitleWrapper>

        <Products>
          {products.map(product => {
            return (
              <ProductWrapper
                key={product.id}
                removeLastChild={!showDefaultProducts}
              >
                <ProductItem product={product} />
              </ProductWrapper>
            );
          })}

          {showDefaultProducts && range(defaultProducts).map((_product, index) => {
            return (
              <ProductWrapper
                key={index}
                removeLastChild={showDefaultProducts}
              >
                <DefaultProductItem key={index} />
              </ProductWrapper>
            );
          })}
        </Products>
      </Grid>
    </StyledContainer>
  );
}
